/**
 * What the widget tells the agent about the visitor — and, in the same file and
 * with the same weight, what it refuses to tell them.
 *
 * This module is a support tool operating on a stranger's browser, on a page
 * whose owner is our customer and whose visitor is not. The GDPR and Estonia's
 * IKS apply to every field below, and the operative test is Art. 5(1)(c), data
 * minimisation: "adequate, relevant and limited to what is NECESSARY in relation
 * to the purposes". The purpose here is exactly one thing — HELPING THIS PERSON
 * WITH THE QUESTION THEY JUST ASKED — and every field in {@link NECESSARY_FIELDS}
 * carries a comment saying what the agent does with it that they could not do
 * without it. That comment is the field's licence to exist. A field whose comment
 * reads "it might be useful" has failed the test and belongs behind consent or
 * nowhere.
 *
 * THE LAWFUL BASIS FOR THE NECESSARY SET IS NOT CONSENT, and getting this
 * backwards is the most common way a widget ends up worse for the visitor. The
 * visitor opened a chat panel and typed a question: that is Art. 6(1)(b),
 * processing necessary to provide a service the data subject asked for. Putting
 * it behind a consent banner would mean a support widget that cannot help until
 * the visitor clicks something, which trains people to click yes to everything
 * and buys them no protection at all. So the necessary set is collected with no
 * banner, no prompt and no way to refuse — and the price of that is that the set
 * has to stay genuinely small, which is what the per-field comments below are
 * for. Anything that is not necessary sits in `consent.ts`, is refused by
 * default, and the widget works completely without it.
 *
 * ── THE COMBINATION PROBLEM, RAISED BY NAME IN A SIBLING MODULE ─────────────
 *
 * `i18n/timezone.ts` says of its own field: "it is ONE field, taken alone. A zone
 * plus a language plus a screen size is the start of a device fingerprint, and
 * this module stops at the zone because combining is the step that turns three
 * innocuous facts into an identifier." That is correct, and THIS module is the
 * one that combines them. Answering it rather than ignoring it:
 *
 *   • The combination is the point of the feature, not an accident of it. An
 *     agent helping with a broken checkout needs the browser AND the screen AND
 *     the page; a fingerprint is the same facts collected for a different purpose.
 *     Purpose limitation (Art. 5(1)(b)) is the line, and it cannot be enforced by
 *     a type — which is why it is written down instead.
 *   • What CAN be done structurally is lower the entropy of each part and refuse
 *     to persist the whole. Every reduction below exists for this: name-and-major
 *     rather than the UA string, one language tag rather than the list, a 50 px
 *     viewport bucket rather than the exact size, OS family rather than version.
 *     Nothing here is written to `localStorage` or to a cookie, so the combined
 *     value never becomes a key that can be looked up again.
 *   • The residual risk is real and is NOT engineered away: an agent's screen
 *     will show several coarse facts about one person at one moment. That is a
 *     support panel, and it is bounded by the conversation it belongs to.
 *
 * ── THE REFUSALS, WHICH ARE THE POINT ───────────────────────────────────────
 *
 * {@link REFUSED_SIGNALS} lists what this module will not read, each with the
 * reason. It is exported as DATA rather than left in prose so that
 * `visitor-context.test.ts` can assert the collected keys never intersect it, and
 * so a visitor-facing "what we do not collect" panel reads from the same list the
 * code obeys. A refusal that lives only in a comment is a refusal that the next
 * feature quietly overrides.
 *
 * ── WHAT THIS FILE PHYSICALLY CANNOT DO, AND WHY THAT IS GOOD ───────────────
 *
 * This bundle runs inside a sandboxed iframe on `widget.<domain>`. It cannot read
 * the host page's URL: `window.parent.location` is cross-origin, and
 * `document.referrer` inside the frame is not the visitor's referrer at all — the
 * loader sets `frame.referrerPolicy = 'origin'` (`src/loader/loader.ts:171`) with
 * the comment "The customer's full page URL is their business", so the frame's
 * own referrer is the host ORIGIN. Reading it and labelling it "where the visitor
 * came from" would be recording a fact about our own iframe and presenting it to
 * an agent as the visitor's browsing history.
 *
 * So {@link HostPageSignals} exists: the three page-level values must be handed
 * in by whoever can honestly obtain them. Today only `pageOrigin` can be —
 * `app/host.ts` learns it from `event.origin` of the handshake, which the browser
 * stamps. `pagePath` and `referrer` have NO transport: `loader/protocol.ts`'s
 * `HostMessage` union carries `init`/`open`/`close`/`theme` and no page fields.
 * They are `null` at every call site today and the reducers below are still
 * written and still tested, because the day someone adds that message is the day
 * a raw URL with a password-reset token in its query string would otherwise reach
 * an agent's screen.
 *
 * ── WHERE ANY OF THIS WOULD BE STORED: NOWHERE, AND THAT IS THE GAP ─────────
 *
 * Verified on disk, not assumed:
 *   • `widget_session` (04_channels.prisma:876-908) has thirteen columns and not
 *     one of them holds visitor context. Its `origin` column already holds the
 *     page origin, so a second copy of that value would be the drift this
 *     repository refuses.
 *   • `person` (03_identity.prisma) carries `locale` and `timezone`. `locale` is
 *     already written by the bootstrap (`widget.service.ts:323`, `pickLocale`).
 *     `timezone` is never written by anything, and — this is the sharp part —
 *     NEITHER COLUMN CARRIES `/// @pii`, so neither appears in
 *     `erasure-inventory.generated.ts` and neither would be nulled by an erasure.
 *     A visitor's IANA zone written there today survives their own erasure
 *     request on a row whose `status` says `erased`.
 *   • `WidgetSessionRequestSchema` (routes/widget.ts:284-343) accepts
 *     `publicKey`, `pageOrigin`, `prechat` and `locale`. There is no context
 *     field, and `prechat` is not a way in: the server builds a zod object from
 *     the brand's configured field rows (`domain/prechat.ts:145-180`), so an
 *     unconfigured key is stripped in silence. Smuggling context through it would
 *     also file machine-read facts under "what the visitor typed", which is the
 *     one label that must stay true.
 *
 * Every one of those needs a migration and a contract change, which this task is
 * not allowed to make. So this module BUILDS the context and does not transmit
 * it. That is a deliberate half, and it is the honest half to build first: the
 * minimisation rules, the truncations and the refusals have to exist before there
 * is anywhere to put the data, or the column arrives first and the rules arrive
 * as a follow-up nobody schedules.
 *
 * WHAT BREAKS IF THIS DESIGN CHANGES. Move a field out of the consent-gated set
 * into the necessary set and the visitor loses the ability to say no to it, with
 * no error anywhere. Widen a truncation and personal data an agent never needed
 * starts appearing in a support panel and, once transmitted, in a database whose
 * erasure job does not know the column exists. Both failures are silent on both
 * sides of the conversation, which is why the tests assert the truncations rather
 * than the happy path.
 */
import { readDeviceTimeZone } from '../i18n/timezone';

import { isGranted, type ConsentState } from './consent';

/** How the agent should imagine the visitor's screen. `null` when unmeasurable. */
export type DeviceClass = 'mobile' | 'tablet' | 'desktop';

/** One entry of `navigator.userAgentData.brands`. */
export interface UserAgentBrand {
  readonly brand: string;
  readonly version: string;
}

/**
 * The three page-level facts that only the HOST PAGE can state honestly.
 *
 * All three default to null, and two of them have no transport today — see the
 * header. They are parameters rather than reads so that the day one becomes
 * available, the only change is at the call site and every rule below already
 * applies to it.
 */
export interface HostPageSignals {
  /**
   * From `event.origin` of the handshake (`app/host.ts`). The browser stamps it
   * and the sender cannot forge it, which is the same property that makes it
   * usable as the `allowed_origins` check's input.
   */
  readonly pageOrigin?: string | null;
  /** The host page's path. No transport today. Truncated hard if one appears. */
  readonly pagePath?: string | null;
  /** The host page's referrer. No transport today. Reduced to an origin. */
  readonly referrer?: string | null;
}

/**
 * The raw inputs, separated from the rules that reduce them.
 *
 * The split exists so the reduction is testable: the unit runner for this app is
 * a hermetic node environment (`packages/config-vitest/src/index.js:51`), so a
 * collector that read `navigator` inline would have its minimisation rules
 * covered by nothing at all.
 */
export interface VisitorSignals {
  /** `navigator.userAgent`. Parsed and DISCARDED — never carried through. */
  readonly userAgent: string | null;
  /** `navigator.userAgentData.brands`, the low-entropy hint. Preferred when present. */
  readonly uaBrands: readonly UserAgentBrand[] | null;
  /** `navigator.userAgentData.mobile`. */
  readonly uaMobile: boolean | null;
  /** `navigator.userAgentData.platform` — already a family, not a version. */
  readonly uaPlatform: string | null;
  /** The FIRST entry of `navigator.languages`, never the list. */
  readonly primaryLanguage: string | null;
  /** `Intl.DateTimeFormat().resolvedOptions().timeZone`. */
  readonly timeZone: string | null;
  readonly viewportWidth: number | null;
  readonly viewportHeight: number | null;
  readonly pageOrigin: string | null;
  readonly pagePath: string | null;
  readonly referrer: string | null;
}

/** Bucketed viewport. Never the exact pixel count — see {@link bucket}. */
export interface Viewport {
  readonly width: number;
  readonly height: number;
}

/**
 * What an agent may be shown. Optional members are ABSENT, not undefined, when
 * consent is missing — `exactOptionalPropertyTypes` is on and the members below
 * are spread conditionally, so `JSON.stringify` of a no-consent context does not
 * even mention the keys the visitor refused.
 */
export interface VisitorContext {
  readonly browser: string | null;
  readonly os: string | null;
  readonly deviceClass: DeviceClass | null;
  readonly viewport: Viewport | null;
  readonly language: string | null;
  readonly timeZone: string | null;
  readonly pageOrigin: string | null;
  readonly pagePath?: string;
  readonly referrerOrigin?: string;
}

export const NECESSARY_FIELDS = [
  'browser',
  'os',
  'deviceClass',
  'viewport',
  'language',
  'timeZone',
  'pageOrigin',
] as const;

export const OPTIONAL_FIELDS = ['pagePath', 'referrerOrigin'] as const;

export type VisitorContextField =
  (typeof NECESSARY_FIELDS)[number] | (typeof OPTIONAL_FIELDS)[number];

/**
 * The per-field record a visitor is entitled to read (Art. 13/15) and the next
 * engineer is obliged to extend.
 *
 * `basis` is the lawful basis in one word: `necessary` means Art. 6(1)(b) and no
 * prompt; a purpose name means Art. 6(1)(a) and a refusable prompt.
 *
 * `storedAs` names the column that already holds the value, or null. It is the
 * shape of the gap and it is asserted by the test rather than described in prose,
 * because the erasure inventory can only reach a column that exists: any field
 * with `storedAs: null` that later gains a column MUST gain a `/// @pii`
 * annotation in the same change, or `contracts:pii` and
 * `erasure-inventory.generated.ts` will both stay green while personal data sits
 * outside every erasure this product can perform.
 *
 * There is deliberately NO human-readable label here. This app's copy is a closed
 * hand-written catalogue (`i18n/messages.ts`, `WIDGET_MESSAGE_IDS`) and the ids a
 * disclosure panel needs do not exist in it yet; inventing English strings in a
 * logic module would put untranslated copy in front of a Ukrainian visitor, which
 * that file argues against at length. The UI maps these keys to catalogue entries
 * when the ids are added.
 */
export interface DisclosureEntry {
  readonly field: VisitorContextField;
  readonly basis: 'necessary' | 'page-context' | 'arrival-context';
  readonly storedAs: string | null;
}

export const VISITOR_CONTEXT_DISCLOSURE: readonly DisclosureEntry[] = Object.freeze([
  /**
   * NECESSARY — the agent's instructions are browser-specific and wrong on the
   * wrong browser. "Clear the site data for this page" is three different menus
   * in Safari, Firefox and Chrome, and an agent who guesses sends the visitor to
   * a menu that is not there.
   *
   * NAME AND MAJOR VERSION ONLY. The full UA string is ~120 characters of build
   * numbers and platform tokens and is one of the highest-entropy values a
   * browser hands out; it is parsed here and then dropped. The major version is
   * kept because "update your browser" and "this is a known bug in 118" both need
   * it, and nothing an agent does needs the patch level.
   */
  Object.freeze({ field: 'browser', basis: 'necessary', storedAs: null } as const),
  /**
   * NECESSARY — the OS decides the instruction as much as the browser does:
   * Ctrl vs Cmd, "Downloads" vs "Files", where a PDF opens. FAMILY ONLY, no
   * version: "Windows" not "Windows NT 10.0; Win64; x64", because no support
   * answer this product gives turns on a Windows build number, and the version
   * is entropy.
   */
  Object.freeze({ field: 'os', basis: 'necessary', storedAs: null } as const),
  /**
   * NECESSARY — "tap the button in the top right" is wrong on a desktop and "the
   * menu is under the hamburger" is wrong on one. It is also the single field
   * that most often explains a complaint before it is described: a form that
   * "does not work" usually works, on a screen the visitor does not have.
   */
  Object.freeze({ field: 'deviceClass', basis: 'necessary', storedAs: null } as const),
  /**
   * NECESSARY — a layout complaint is not reproducible without it, and the
   * visitor cannot supply it: nobody knows their own viewport. BUCKETED, never
   * exact; see {@link bucket} for the size of the bucket and the cost of it.
   */
  Object.freeze({ field: 'viewport', basis: 'necessary', storedAs: null } as const),
  /**
   * NECESSARY — replying in a language the visitor cannot read is not support.
   * This is the visitor's own PRIMARY browser tag, and the panel's own locale
   * cannot stand in for it: `i18n/locale.ts` filters the panel to the brand's
   * STAFFED set, so a Polish visitor shown English tells the agent nothing about
   * what that person actually reads, and whether to reach for a translator is
   * exactly the decision being made.
   *
   * ONE TAG, NEVER `navigator.languages`. The ordered list of a person's
   * languages is a well-known fingerprinting vector and answers a question
   * nobody asked.
   *
   * `storedAs` is null even though `person.locale` exists: that column holds the
   * RESOLVED panel locale from `pickLocale`, which is a different value, and
   * overwriting it with the browser tag would silently change what the console
   * renders in.
   */
  Object.freeze({ field: 'language', basis: 'necessary', storedAs: null } as const),
  /**
   * NECESSARY — "we will call you at 18:00" is the failure this field exists to
   * prevent, and it is a failure the visitor discovers by missing the call.
   * Every timestamp in the transcript is UTC; rendering one, or agreeing to one,
   * needs the visitor's zone.
   *
   * A TIMEZONE IS A COARSE LOCATION HINT, and that is the trade named rather than
   * skipped. The narrower field would be the UTC OFFSET, which is a smaller
   * disclosure — but an offset is wrong on both sides of a DST change, which is
   * precisely the appointment this field exists to get right, so the zone is kept
   * and the reason is written down. The zone is also the only location-shaped
   * value in this module: {@link REFUSED_SIGNALS} refuses geolocation and refuses
   * IP-derived location, so a zone is as close as this product gets, and it is
   * accurate to a continent rather than to a street.
   *
   * `storedAs` is null and `person.timezone` is why this matters: that column
   * exists, is never written, and carries no `/// @pii`, so a value written there
   * today would outlive its own subject's erasure.
   */
  Object.freeze({ field: 'timeZone', basis: 'necessary', storedAs: null } as const),
  /**
   * NECESSARY — a brand may staff several sites under one widget key, and "which
   * of your products is this about" is the first thing an agent has to know. It
   * is browser-vouched (`event.origin`) rather than claimed, so it is also the
   * only page-level value here that cannot be lied about.
   *
   * THE ONLY FIELD WITH A COLUMN, and therefore the only one that must NOT be
   * transmitted: `widget_session.origin` already holds exactly this value, put
   * there by the bootstrap that checked it. Sending a second copy would create
   * two places to erase and two answers to "which site was this".
   */
  Object.freeze({
    field: 'pageOrigin',
    basis: 'necessary',
    storedAs: 'widget_session.origin',
  } as const),
  /**
   * CONSENT (`page-context`) — the page path. Refusable, absent by default, and
   * the widget is complete without it: the agent asks "which page?" and the
   * visitor answers, which is how every support conversation worked before this
   * field existed.
   *
   * QUERY STRING AND FRAGMENT ARE CUT, ALWAYS, before consent is even consulted
   * — see {@link pagePath}. What survives is a path, and a path can still carry
   * an order number or a username. That residual risk is the whole reason this is
   * consent-gated instead of necessary: the module can strip what it can
   * recognise, and cannot recognise that `/o/8814` is somebody's purchase.
   */
  Object.freeze({ field: 'pagePath', basis: 'page-context', storedAs: null } as const),
  /**
   * CONSENT (`arrival-context`) — where the visitor came from, ORIGIN ONLY.
   * Refusable, absent by default, separately refusable from the page path
   * precisely because it is a different and more sensitive question: a referrer
   * is a statement about where this person was BEFORE this site.
   *
   * The path and query are cut for a reason with a name:
   * `https://www.google.com/search?q=how+do+i+cancel+my+subscription` is a
   * sentence the visitor wrote to somebody else, and can be a health or a
   * financial disclosure. `https://www.google.com` is the fact that is useful.
   */
  Object.freeze({ field: 'referrerOrigin', basis: 'arrival-context', storedAs: null } as const),
]);

/**
 * The list this module will not read. SLUGS ONLY — the reasons are the doc
 * comments below, and that split is a byte decision with a rule behind it.
 *
 * The first draft carried each reason as a `because` string on the entry, so the
 * refusals could be rendered and asserted from one place. Measured with esbuild
 * (bundle, minify, esm, es2022) over this module and `consent.ts`, that draft
 * cost 7563 B raw / 3121 B gzip / 2536 B brotli against 4957 / 1943 / 1694 with
 * the prose moved into comments — a delta of 2606 B raw and 842 B brotli of
 * English prose, in a bundle a visitor downloads on a customer's page, that NO UI
 * COULD LEGALLY RENDER — this app's
 * copy is a closed hand-written catalogue in two locales (`i18n/messages.ts`),
 * so an English sentence shipped as data is a sentence a Ukrainian visitor would
 * be shown untranslated. A comment costs zero bytes and is read by the only
 * audience that needed it, which is whoever adds the next field.
 *
 * What stays as data is the slug, because that is what the test asserts against
 * and what a translated transparency panel would key its message id off.
 */
export const REFUSED_SIGNALS: readonly string[] = Object.freeze([
  /**
   * `navigator.geolocation` is a street-level fix AND it prompts, so asking for
   * it inside a support panel is also a permission dialog the visitor did not
   * expect from a chat button. No support answer this product gives needs to know
   * where a person is standing; the appointment problem — the only one that looked
   * like it needed location — is solved by the IANA zone, which is accurate to a
   * continent.
   */
  'geolocation',
  /**
   * The same objection with the prompt removed, which makes it worse rather than
   * better: the visitor cannot decline something they are never asked about. The
   * API sees an IP because TCP requires one; resolving it to a city would be a NEW
   * PURPOSE for a value that exists only as a transport necessity, and a new
   * purpose needs its own basis and its own notice. Named here even though the
   * resolution would happen server-side, because this is the list the next person
   * reads before adding "just the country".
   */
  'ip-derived-location',
  /**
   * A device fingerprint identifies a person WITHOUT storing anything, which is
   * exactly why it is not the clever way around consent it looks like: EDPB
   * Guidelines 2/2023 read ePrivacy Art. 5(3) as covering access to information on
   * a visitor's device whether or not a cookie is involved. It also answers no
   * support question at all, so there is nothing on the other side of the trade.
   */
  'canvas-webgl-audio-fingerprint',
  /**
   * Nothing this module produces is written to `localStorage` or to a cookie, so
   * nothing here can follow a visitor to another site. The two records this app
   * does keep — the session cache and the read pointer — both use `sessionStorage`
   * on the widget origin and die with the tab, and this module adds no third
   * store. An identifier that outlives the tab is one field away from being a
   * tracker, and the field is usually added by someone fixing a re-prompt.
   */
  'cross-site-identifier',
  /**
   * A query string is where the web puts order ids, email addresses,
   * password-reset tokens and search terms. The origin is what the agent needs and
   * the path is what they can ask for; everything after the first `?` or `#` is
   * cut in {@link stripQuery} before consent is even consulted, so this refusal
   * holds even for a visitor who agreed to everything.
   */
  'full-page-url',
  /**
   * Parsed for a family and a major version, then dropped — {@link resolveBrowser}
   * never returns the string and {@link VisitorContext} has nowhere to put it. The
   * string itself is ~120 characters of build and platform tokens, one of the
   * highest-entropy values a browser hands out, and its build numbers change no
   * support answer.
   */
  'full-user-agent-string',
  /**
   * The ordered list of a person's languages is a classic fingerprint component
   * and answers a question nobody asked. The head of the list answers the only
   * question an agent has, which is what language to reply in, and
   * {@link readVisitorSignals} takes that head at the read so the list never
   * enters this module's data at all.
   */
  'navigator-languages-list',
  /**
   * `navigator.userAgentData.getHighEntropyValues()` is named after the problem.
   * The default low-entropy brands are read instead, and are PREFERRED over the UA
   * string precisely because they are the coarser of the two sources.
   */
  'high-entropy-client-hints',
  /**
   * `deviceMemory`, `hardwareConcurrency`, screen resolution, battery level,
   * connection type and plugin lists: entropy with no support value, and several
   * of them are the classic fingerprint components. The viewport — bucketed — is
   * the one screen fact an agent actually reasons about, and it is in the
   * necessary set with its own justification.
   */
  'device-capabilities',
]);

/**
 * Everything after the first `?` or `#`, gone.
 *
 * String surgery rather than `new URL`, and that is deliberate twice over: the
 * input may be a bare path with no origin to parse against, and `new URL` throws
 * on input it dislikes — a widget must not be taken down by the shape of a
 * customer's URL. There is no regex here either, so there is no pattern for the
 * `regex-safety` gate to reason about and no backtracking on a long path.
 */
function stripQuery(value: string): string {
  const cut = Math.min(
    ...[value.indexOf('?'), value.indexOf('#')].filter((at) => at >= 0).concat(value.length),
  );
  return value.slice(0, cut);
}

/**
 * A path an agent can read, capped.
 *
 * The cap is not cosmetic. Some frameworks encode state into path segments, and
 * an uncapped path is an unbounded field of unknown content on its way to a
 * support panel. 200 characters is longer than any path a human navigates to and
 * short enough that "the rest of it" is visibly missing rather than silently
 * present. TRUNCATED WITHOUT AN ELLIPSIS MARKER: a marker would be a character an
 * agent might read as part of the URL, and the length is the signal.
 */
const PATH_MAX_LENGTH = 200;

function pagePath(raw: string | null): string | null {
  if (raw === null) {
    return null;
  }
  const path = stripQuery(raw).slice(0, PATH_MAX_LENGTH);
  return path === '' ? null : path;
}

/**
 * A referrer reduced to `scheme://host[:port]` and nothing else.
 *
 * Only `http` and `https` survive. Anything else — `android-app://`,
 * `chrome-extension://`, a bare `about:` — is dropped rather than passed through,
 * because those name an APPLICATION the visitor has installed, which is a device
 * fact and not a page the agent can look at.
 *
 * USERINFO IS CUT, and it is the sharpest thing this function does.
 * `https://ada:hunter2@intranet.example/` reduces to `https://intranet.example` —
 * a username and a password, in a field an agent reads, is the worst single
 * disclosure either module could produce. Browsers strip credentials from a real
 * `Referer` header, so this looks unreachable; it is written anyway because the
 * input to this function is a string handed in by the HOST PAGE
 * ({@link HostPageSignals}), and "the browser would never send that" stops being
 * true the moment the value stops coming from the browser.
 */
function referrerOrigin(raw: string | null): string | null {
  if (raw === null) {
    return null;
  }
  const scheme = raw.startsWith('https://')
    ? 'https://'
    : raw.startsWith('http://')
      ? 'http://'
      : null;
  if (scheme === null) {
    return null;
  }
  const rest = raw.slice(scheme.length);
  // Authority first, then userinfo — in that order, because an `@` may legally
  // appear later in a path or a query and cutting on the last one would take
  // most of the URL with it.
  const slash = stripQuery(rest).indexOf('/');
  const authority = slash >= 0 ? rest.slice(0, slash) : stripQuery(rest);
  const at = authority.indexOf('@');
  const host = at >= 0 ? authority.slice(at + 1) : authority;
  return host === '' ? null : `${scheme}${host}`;
}

/**
 * Viewport bucket, in CSS pixels.
 *
 * 50 px, floored. The entropy argument is an ESTIMATE and is labelled as one:
 * exact viewport dimensions are commonly cited among the strongest single
 * components of a browser fingerprint, and flooring to 50 collapses the plausible
 * range into a few hundred buckets rather than tens of thousands. It is a
 * reduction, not a solution, and it is worth having because the alternative —
 * dropping the field — costs the agent the one screen fact they reason about.
 *
 * THE COST, NAMED: a bucket boundary can land on the wrong side of a CSS
 * breakpoint. A 768 px viewport reports 750, and a brand whose tablet breakpoint
 * is 768 will read that as the narrow layout. So the number is a HINT, and an
 * agent debugging a layout has to ask. Flooring rather than rounding is chosen so
 * the error always points the same way: reported width is never greater than the
 * real one, so an agent is never told the visitor has room they do not have.
 */
const VIEWPORT_BUCKET_PX = 50;

function bucket(value: number): number {
  return Math.max(0, Math.floor(value / VIEWPORT_BUCKET_PX) * VIEWPORT_BUCKET_PX);
}

/**
 * Browser families, most specific first.
 *
 * Order is load-bearing and is the usual UA-sniffing trap: every Chromium browser
 * claims `Chrome/`, and every WebKit-derived one claims `Safari/`. Edge, Opera
 * and Samsung must be tested before Chrome, and Safari is not in this table at
 * all — see {@link resolveBrowser}.
 */
const BROWSER_PATTERNS: readonly (readonly [string, RegExp])[] = Object.freeze([
  Object.freeze(['Edge', /(?:^|[\s(;])Edg(?:e|A|iOS)?\/(\d{1,4})/u] as const),
  Object.freeze(['Opera', /(?:^|[\s(;])OPR\/(\d{1,4})/u] as const),
  Object.freeze(['Samsung Internet', /(?:^|[\s(;])SamsungBrowser\/(\d{1,4})/u] as const),
  Object.freeze(['Firefox', /(?:^|[\s(;])(?:Firefox|FxiOS)\/(\d{1,4})/u] as const),
  Object.freeze(['Chrome', /(?:^|[\s(;])(?:Chrome|CriOS)\/(\d{1,4})/u] as const),
]);

/**
 * Safari's marketing version, which is the only version of Safari worth naming.
 *
 * `(?:^|[\s(;])` AND NOT `\b`, here and in every pattern above. `axon/regex-unicode`
 * bans the ASCII word boundary outright and it is right to: `\b` sits between a
 * Latin letter and a Cyrillic one, so a pattern anchored on it shifts silently on
 * a UA string carrying a non-ASCII token — and a UA string is attacker-supplied.
 * The explicit separator class says what the boundary actually is in this grammar:
 * a UA token starts at the beginning, or after a space, a `(` or a `;`.
 */
const SAFARI_VERSION = /(?:^|[\s(;])Version\/(\d{1,4})/u;

/**
 * OS families, most specific first, and NO VERSIONS.
 *
 * iOS before macOS (an iPhone's string contains `Mac OS X`) and Android before
 * Linux (Android's contains `Linux`). A known and accepted miss: an iPad in
 * desktop mode is indistinguishable from a Mac in its UA string, so it reports
 * macOS. That is wrong and it is cheap — the device class still says tablet —
 * and the alternative is touch-point sniffing, which is a fingerprinting signal
 * bought to fix a cosmetic label.
 *
 * LEADING SEPARATOR ONLY, no trailing one. `\b` is banned (see
 * {@link SAFARI_VERSION}), and a trailing boundary would buy nothing here anyway:
 * these are `.test` calls with no capture, and the families are ordered so that
 * the specific token is tried before the one it contains.
 */
const OS_PATTERNS: readonly (readonly [string, RegExp])[] = Object.freeze([
  Object.freeze(['iOS', /(?:^|[\s(;])(?:iPhone|iPad|iPod)/u] as const),
  Object.freeze(['Android', /(?:^|[\s(;])Android/u] as const),
  Object.freeze(['Windows', /(?:^|[\s(;])Windows NT/u] as const),
  Object.freeze(['macOS', /(?:^|[\s(;])Mac OS X/u] as const),
  Object.freeze(['Linux', /(?:^|[\s(;])Linux/u] as const),
]);

/**
 * A GREASE entry — Chromium deliberately injects a fake brand so that parsers
 * cannot assume a fixed list. Dropping it by shape (`Not` … `Brand`) rather than
 * by exact string: the punctuation between the two words is randomised on
 * purpose, and matching the literal is how a parser breaks on a Chrome update.
 */
function isGrease(brand: string): boolean {
  return brand.includes('Not') && brand.includes('Brand');
}

/**
 * Browser from the LOW-ENTROPY hint when the browser offers one, from the UA
 * string only as a fallback.
 *
 * Preferring `userAgentData.brands` is the privacy-correct order, not just the
 * modern one: the hint is a short name and a major version by construction, while
 * the string is everything the browser has ever been asked to admit. Chromium
 * lists `Chromium` alongside its real brand, so the non-Chromium entry wins where
 * there is one — `Google Chrome 131`, not `Chromium 131`.
 */
function resolveBrowser(signals: VisitorSignals): string | null {
  const brands = (signals.uaBrands ?? []).filter((entry) => !isGrease(entry.brand));
  const preferred = brands.find((entry) => entry.brand !== 'Chromium') ?? brands[0];
  if (preferred !== undefined && preferred.brand !== '') {
    const major = /^(\d{1,4})/u.exec(preferred.version)?.[1] ?? null;
    return major === null ? preferred.brand : `${preferred.brand} ${major}`;
  }

  const ua = signals.userAgent;
  if (ua === null) {
    return null;
  }
  for (const [name, pattern] of BROWSER_PATTERNS) {
    const major = pattern.exec(ua)?.[1];
    if (major !== undefined) {
      return `${name} ${major}`;
    }
  }

  // SAFARI LAST, AND IN TWO TESTS RATHER THAN ONE PATTERN. Real Safari looks
  // like `Version/17.5 Mobile/15E148 Safari/604.1` — arbitrary tokens sit
  // between the two halves, so a single pattern spanning them needs a `.*`
  // against a string an attacker controls, which is the shape the `regex-safety`
  // gate exists to keep out. Two anchored tests cannot backtrack and cost one
  // `includes`. Last, because every Chromium UA also contains `Safari/` and an
  // eager pattern would report Chrome as Safari.
  const safari = ua.includes('Safari/') ? SAFARI_VERSION.exec(ua)?.[1] : undefined;
  return safari === undefined ? null : `Safari ${safari}`;
}

/** Platform hint first, for the same reason and with the same ordering trap avoided. */
function resolveOs(signals: VisitorSignals): string | null {
  const platform = signals.uaPlatform;
  if (platform !== null && platform !== '') {
    return platform;
  }
  const ua = signals.userAgent;
  if (ua === null) {
    return null;
  }
  for (const [name, pattern] of OS_PATTERNS) {
    if (pattern.test(ua)) {
      return name;
    }
  }
  return null;
}

/**
 * Widest viewport this module is willing to call a phone, and the narrowest it
 * will call a tablet. Both are read against the BUCKETED width, so the boundary
 * an agent sees and the boundary this function used are the same number.
 */
const MOBILE_MAX_WIDTH_PX = 600;
const DESKTOP_MIN_WIDTH_PX = 1000;

/**
 * Device class from the mobile HINT where there is one, from the viewport only
 * where there is not — and the difference between those two answers is stated
 * because it changes what the answer MEANS.
 *
 * `userAgentData.mobile` is a statement about the hardware. A viewport is a
 * statement about the window, so a desktop browser dragged narrow reports
 * `mobile`. That is not a bug for this purpose: the agent's question is what the
 * visitor can SEE, and a 500 px window is showing the narrow layout whatever it
 * is running on. It IS a bug for any purpose that reads this as "what device does
 * this person own", and nothing should.
 */
function resolveDeviceClass(signals: VisitorSignals, width: number | null): DeviceClass | null {
  if (signals.uaMobile === true) {
    return width !== null && width >= MOBILE_MAX_WIDTH_PX ? 'tablet' : 'mobile';
  }
  if (width === null) {
    // No hint and nothing to measure. `desktop` would be a guess dressed as a
    // fact, and an agent who reads a guess gives instructions for a screen the
    // visitor does not have.
    return signals.uaMobile === false ? 'desktop' : null;
  }
  if (width < MOBILE_MAX_WIDTH_PX) {
    return 'mobile';
  }
  return width < DESKTOP_MIN_WIDTH_PX ? 'tablet' : 'desktop';
}

/**
 * A single BCP-47 tag, capped at the longest well-formed tag this product will
 * ever act on. The cap is not validation — `resolveWidgetLocale` does the
 * narrowing — it is a bound on an unbounded string from a hostile-capable source.
 */
const LANGUAGE_MAX_LENGTH = 35;

/**
 * Build the context. PURE: no globals, no clock, no storage, no network.
 *
 * `consent` is a required parameter with no default. A default would make the
 * safe call and the unsafe call look identical at the call site, and the unsafe
 * one is the one that silently collects. The compiler asks for the consent state
 * every time instead.
 */
export function buildVisitorContext(
  signals: VisitorSignals,
  consent: ConsentState,
): VisitorContext {
  const width = signals.viewportWidth === null ? null : bucket(signals.viewportWidth);
  const height = signals.viewportHeight === null ? null : bucket(signals.viewportHeight);

  const path = isGranted(consent, 'page-context') ? pagePath(signals.pagePath) : null;
  const arrival = isGranted(consent, 'arrival-context') ? referrerOrigin(signals.referrer) : null;

  const language =
    signals.primaryLanguage === null || signals.primaryLanguage === ''
      ? null
      : signals.primaryLanguage.slice(0, LANGUAGE_MAX_LENGTH);

  return {
    browser: resolveBrowser(signals),
    os: resolveOs(signals),
    deviceClass: resolveDeviceClass(signals, width),
    viewport: width === null || height === null ? null : { width, height },
    language,
    timeZone: signals.timeZone === null || signals.timeZone === '' ? null : signals.timeZone,
    pageOrigin: signals.pageOrigin,
    // SPREAD CONDITIONALLY, and not `pagePath: path ?? undefined`.
    // `exactOptionalPropertyTypes` is on, so the two are different types — and
    // they are different VALUES to every consumer: an absent key is absent from
    // `JSON.stringify`, from `Object.keys` and from any panel that iterates the
    // object, whereas an explicit `undefined` is a key the visitor refused that
    // still shows up in the list of things we hold about them.
    ...(path === null ? {} : { pagePath: path }),
    ...(arrival === null ? {} : { referrerOrigin: arrival }),
  };
}

function readString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Read what THIS browser is willing to say, absorbing every way it can refuse.
 *
 * Every read is defensive and the reason is not hypothetical: a privacy-hardened
 * browser or an extension can make any of these throw or return a shape nobody
 * documented, and this app's own storage helpers already absorb the same class of
 * failure (`api/session-store.ts`, `app/seen.ts`, both with the note that access
 * itself throws in some privacy modes). A visitor who hardened their browser must
 * still get a working support panel — a collector that takes the widget down is a
 * collector that punishes exactly the people who cared.
 *
 * It also runs, and is tested, where none of these globals exist at all: the unit
 * runner is `environment: 'node'`, so the no-browser path is the DEFAULT path
 * here rather than an edge case somebody has to remember to exercise.
 */
export function readVisitorSignals(fromHost: HostPageSignals = {}): VisitorSignals {
  const nav = (globalThis as { navigator?: unknown }).navigator;
  const navigatorRecord =
    typeof nav === 'object' && nav !== null ? (nav as Record<string, unknown>) : {};

  const uaData = navigatorRecord['userAgentData'];
  const uaDataRecord =
    typeof uaData === 'object' && uaData !== null ? (uaData as Record<string, unknown>) : {};

  const rawBrands = uaDataRecord['brands'];
  const uaBrands = Array.isArray(rawBrands)
    ? Object.freeze(
        (rawBrands as readonly unknown[])
          .filter(
            (entry): entry is Record<string, unknown> =>
              typeof entry === 'object' && entry !== null,
          )
          .map((entry) => ({
            brand: typeof entry['brand'] === 'string' ? entry['brand'] : '',
            version: typeof entry['version'] === 'string' ? entry['version'] : '',
          }))
          .filter((entry) => entry.brand !== ''),
      )
    : null;

  // `navigator.languages[0]` and NOT the array. Taking the head here rather than
  // in the reducer means the list never enters this module's data at all, so
  // there is no later refactor that can start carrying it by accident.
  const languages = navigatorRecord['languages'];
  const primaryLanguage =
    Array.isArray(languages) && typeof languages[0] === 'string' && languages[0] !== ''
      ? languages[0]
      : readString(navigatorRecord, 'language');

  const win = (globalThis as { window?: unknown }).window;
  const windowRecord =
    typeof win === 'object' && win !== null ? (win as Record<string, unknown>) : {};
  const innerWidth = windowRecord['innerWidth'];
  const innerHeight = windowRecord['innerHeight'];

  return {
    userAgent: readString(navigatorRecord, 'userAgent'),
    uaBrands: uaBrands === null || uaBrands.length === 0 ? null : uaBrands,
    uaMobile: typeof uaDataRecord['mobile'] === 'boolean' ? uaDataRecord['mobile'] : null,
    uaPlatform: readString(uaDataRecord, 'platform'),
    primaryLanguage,
    // DELEGATED, NOT REIMPLEMENTED. `i18n/timezone.ts` reads the same value for
    // the panel's own rendering, and it handles the two runtimes this one would
    // have got wrong: `resolvedOptions()` returning no `timeZone` at all (the lib
    // types promise a `string`, so a hand-rolled guard here reads as unnecessary
    // and gets deleted), and an `Intl` an extension has replaced with something
    // that throws. Two readers of one device fact would agree until the day one
    // was fixed.
    //
    // THE COUPLING IS DELIBERATE AND ITS COST IS NAMED: that module is owned by
    // the locale half of this feature, so this file now fails to COMPILE if its
    // signature moves. That is the intended failure — loud, at build time, in the
    // same tsconfig program — and it is the better half of the trade against two
    // copies of a zone read that drift in silence.
    //
    // The RAW identifier is taken, not `visitorTimeZone()`. That function answers
    // with a validated `TimeZone` branded for a formatter and substitutes UTC when
    // it cannot vouch for one; here the honest answer to "what did the device say"
    // is null, because telling an agent the visitor is on UTC when the browser
    // said nothing is a fact invented to fill a field.
    timeZone: readDeviceTimeZone(),
    viewportWidth: typeof innerWidth === 'number' && innerWidth > 0 ? innerWidth : null,
    viewportHeight: typeof innerHeight === 'number' && innerHeight > 0 ? innerHeight : null,
    // The iframe cannot obtain any of these honestly. See the header: `pageOrigin`
    // arrives over the handshake, and the other two have no transport at all.
    pageOrigin: fromHost.pageOrigin ?? null,
    pagePath: fromHost.pagePath ?? null,
    referrer: fromHost.referrer ?? null,
  };
}
